import { useEffect, useRef, type RefObject } from 'react';

// Closes a dropdown/suggestion list when the user presses outside `ref` or tabs focus
// out of it. Replaces the `onBlur={() => setTimeout(close, 150)}` pattern, which dropped
// clicks held longer than 150ms and closed the list when its scrollbar was dragged.
// Listens on the element's own document so it works in the detached viewer window.
export function useDismissOnOutsideClick(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    const doc = el.ownerDocument;
    const onMouseDown = (e: MouseEvent) => {
      if (!el.contains(e.target as Node)) onCloseRef.current();
    };
    // relatedTarget is null when focus moves to a non-focusable spot (e.g. the list's
    // own items or scrollbar); the mousedown check covers real outside clicks.
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && !el.contains(next)) onCloseRef.current();
    };
    doc.addEventListener('mousedown', onMouseDown);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      doc.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('focusout', onFocusOut);
    };
  }, [ref, open]);
}
